import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { State } from '@app/store/state/state';
import {
  fetchItineraryFlightAction,
  selectSectorItineraryDetailAction,
  selectFlightItineraryDetailAction,
  fetchSeatMapFlightAction,
  selectSeatsInFlightAction,
  removeSeatInFlightAction,
  selectSeatPassengerInFlightAction,
  fetchSaveSeatsPassengersInFlightAction,
  selectPreviewSeatInFlightAction,
  removePreviewSeatInFlightAction,
  closeErrorSeatMapAction
} from '@seat-map/store/actions/seat-map.actions';
import { ItineraryDetail, AirValidationSector, AirValidationFlightsSector } from '@seat-map/entities/itinerary-detail';
import { seatMapRootSelector, selectSectorAndFlightSelected } from '@seat-map/store/selectors/seat-map-detail.selector';
import { getSeatMapState } from '@seat-map/store/selectors/ui.selector';
import { PassengersSeatMap, PassengersSeats, SeatMap, SeatMapErrors, SeatMapTransactions } from '@seat-map/entities/seat-map';
import { seatMapSelector } from '@seat-map/store/selectors/seat-map.selector';

@Injectable()
export class SeatMapFacade {

  constructor(private readonly store: Store<State>) { }

  public isLoadingSeatMap$: Observable<boolean> = this.store
    .pipe(
      select(getSeatMapState),
      map((state) => state.isLoadingSeatMap)
    );

  public isLoadingSeatMapFlight$: Observable<boolean> = this.store
    .pipe(
      select(getSeatMapState),
      map((state) => state.isLoadingSeatMapFlight)
    );

  public showError$: Observable<boolean> = this.store
    .pipe(
      select(getSeatMapState),
      map((state) => state.showError)
    );

  public itineraryDetail$: Observable<ItineraryDetail> = this.store.pipe(
    select(seatMapRootSelector),
    map(state => state.itineraryDetail)
  );

  public seatMap$: Observable<SeatMap> = this.store.pipe(
    select(seatMapSelector),
    map(state => state)
  );

  public seatMapSeats$: Observable<PassengersSeats[]> = this.store.pipe(
    select(seatMapSelector),
    map(state => state.Seats)
  );

  public sectorAndFlightSelected$: Observable<AirValidationSector> = this.store.pipe(
    select(selectSectorAndFlightSelected),
    map(state => state)
  );

  public seatMapError$: Observable<SeatMapErrors> = this.store.pipe(
    select(seatMapRootSelector),
    map(state => state.errors)
  );

  public seatMapTransactions$: Observable<SeatMapTransactions[]> = this.store.pipe(
    select(seatMapRootSelector),
    map(state => state.transactions)
  );

  public fetchItineraryDetail(itineraryId: string, flightIdx: number): void {
    this.store.dispatch(fetchItineraryFlightAction({ itineraryId, flightIdx }));
  }

  public fetchSeatMapFlight(itineraryId: number, airReservationId: number,
    flightIdx: number, passengers: PassengersSeatMap[]): void {
    this.store.dispatch(fetchSeatMapFlightAction({ itineraryId, airReservationId, flightIdx, passengers }));
  }

  public selectSectorItineraryDetail(sector: number): void {
    this.store.dispatch(selectSectorItineraryDetailAction({ sector }));
  }

  public selectFlightItineraryDetail(flight: AirValidationFlightsSector): void {
    this.store.dispatch(selectFlightItineraryDetailAction({ flight }));
  }

  public selectSeatInFlightAction(seatNumbers: string[]): void {
    this.store.dispatch(selectSeatsInFlightAction({ seatNumbers }));
  }

  public removeSeatInFlightAction(seatNumber: string): void {
    this.store.dispatch(removeSeatInFlightAction({ seatNumber }));
  }

  public selectSeatPassengerInFlightAction(seatNumber: string, passenger: PassengersSeatMap): void {
    this.store.dispatch(selectSeatPassengerInFlightAction({ seatNumber, passenger }));
  }

  public fetchSaveSeatsPassengersInFlightAction(seatMap: SeatMap, itinerary: ItineraryDetail,
    flightIdx: number, nextFlight: boolean): void {
    this.store.dispatch(fetchSaveSeatsPassengersInFlightAction({ seatMap, itinerary, flightIdx, nextFlight }));
  }

  public selectPreviewSeatInFlightAction(seatNumber: string): void {
    this.store.dispatch(selectPreviewSeatInFlightAction({ seatNumber }));
  }

  public removePreviewSeatInFlightAction(seatNumber: string): void {
    this.store.dispatch(removePreviewSeatInFlightAction({ seatNumber }));
  }

  public closeErrorSeatMapAction(): void {
    this.store.dispatch(closeErrorSeatMapAction());
  }

}
